import { defineStore } from 'pinia'
import { useToast } from 'vue-toastification'
import socket from '../services/socket'
import { useSessionStore } from './session'

// 🔊 Singleton de AudioContext — los navegadores limitan el número de contextos activos.
// Crearlo una vez y reutilizarlo evita warnings y pérdida de sonido tras muchas notificaciones.
let _audioCtx = null
function getAudioCtx() {
    if (!_audioCtx || _audioCtx.state === 'closed') {
        _audioCtx = new (window.AudioContext || window.webkitAudioContext)()
    }
    // Reanudar si el navegador lo suspendió por inactividad (política de autoplay)
    if (_audioCtx.state === 'suspended') {
        _audioCtx.resume()
    }
    return _audioCtx
}

export const useNotificationStore = defineStore('notificacion', {
    state: () => ({
        permisoConcedido: false,
        _inicializado: false
    }),

    actions: {
        async init() {
            if (this._inicializado) return

            const toast = useToast()
            const session = useSessionStore()

            // 1. Pedir permiso al navegador si es posible
            if ('Notification' in window) {
                if (Notification.permission === 'granted') {
                    this.permisoConcedido = true
                } else if (Notification.permission !== 'denied') {
                    const permission = await Notification.requestPermission()
                    this.permisoConcedido = (permission === 'granted')
                }
            }

            // 2. Función para unirse a salas — se ejecuta en cada conexión/reconexión
            const unirseASalas = () => {
                if (!session.user?.id) return

                // Sala personal (para notificaciones dirigidas como asignaciones)
                socket.emit('join_user', session.user.id)

                // Sala por rol: admins reciben todo, el resto solo su línea
                if (session.user.rol === 'admin') {
                    socket.emit('join_admin')
                } else if (session.user.linea_id) {
                    socket.emit('join_linea', session.user.linea_id)
                }
            }

            // Unirse cuando el socket se conecte (o reconecte)
            socket.on('connect', unirseASalas)

            // Si ya está conectado, unirse de inmediato
            if (socket.connected) {
                unirseASalas()
            }

            // 3. Escuchar notificaciones dirigidas de OTs (Asignaciones)
            socket.on('notificacion_ot', (datos) => {
                const { id_ot, nro_maquina, mensaje } = datos
                const titulo = `🔔 Nueva Tarea: Máquina ${nro_maquina}`
                const cuerpo = `OT #${id_ot}: ${mensaje}`

                // Mostrar Toast (siempre visible en la app)
                toast.info(`${titulo}\n${cuerpo}`, { timeout: 10000 })

                // Mostrar Notificación de Sistema (si tiene permiso y no está en foco)
                this.dispararNotificacionSistema(titulo, cuerpo)

                // Reproducir sonido
                this.reproducirAlerta()
            })

            // 4. Escuchar actualizaciones de OTs (filtrado por sala en el servidor)
            socket.on('ot_actualizada', (data) => {
                toast(data.detalles, {
                    type: data.tipo || 'info',
                    timeout: 5000
                })
            })

            // 5. Escuchar alertas de GPS descontinuados / Beta / Boletos extra
            socket.on('gate_deprecated_alert', (alerta) => {
                /*const esAdminOTecnico = ['admin', 'tecnico'].includes(session.user?.rol)
                if (!esAdminOTecnico) return

                const userLinea = session.user?.linea_id ? parseInt(session.user.linea_id) : null
                if (userLinea !== null && alerta.linea !== userLinea) return

                const icono = alerta.type === 'GPS_BETA' ? '⚠️' : 'ℹ️'
                const titulo = `${icono} ${alerta.titulo}: Bus ${alerta.bus} (L${alerta.linea})`
                const cuerpo = alerta.detalle || alerta.message

                toast.warning(`${titulo}\n${cuerpo}`, { timeout: 10000 })
                this.dispararNotificacionSistema(titulo, cuerpo)
                this.reproducirAlerta()*/
            })

            this._inicializado = true
        },

        dispararNotificacionSistema(titulo, cuerpo) {
            if (this.permisoConcedido && document.hidden) {
                new Notification(titulo, {
                    body: cuerpo,
                    icon: '/vite.svg'
                })
            }
        },

        reproducirAlerta() {
            try {
                const audioCtx = getAudioCtx()
                const oscillator = audioCtx.createOscillator()
                const gainNode = audioCtx.createGain()

                oscillator.type = 'sine'
                oscillator.frequency.setValueAtTime(880, audioCtx.currentTime) // Nota A5
                gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime)

                oscillator.connect(gainNode)
                gainNode.connect(audioCtx.destination)

                oscillator.start()
                // Segundo pitido breve para que sea más llamativo
                setTimeout(() => {
                    oscillator.frequency.setValueAtTime(1100, audioCtx.currentTime)
                    setTimeout(() => oscillator.stop(), 150)
                }, 150)
            } catch (e) {
                // Silenciar error de audio — no es crítico
            }
        },

        destroy() {
            socket.off('connect')
            socket.off('notificacion_ot')
            socket.off('ot_actualizada')
            socket.off('gate_deprecated_alert')
            this._inicializado = false
        }
    }
})

